import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Paper,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useHistory, useLocation } from 'react-router-dom';
import identityService from '../../services/identityService';
import '../IdentityLogin/styles.css';

function IdentityResetPassword() {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const identityUrl = process.env.REACT_APP_IDENTITY_URL || "https://auth.rentbeets.com";

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await identityService.resetPassword({
        token,
        password: formData.password,
      });

      if (response.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = `${identityUrl}/identity/login?passwordReset=true`;
        }, 3000);
      } else {
        setError(response.message || 'Password reset failed');
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Invalid or expired reset token. Please request a new password reset link.');
      } else {
        setError(err.response?.data?.message || 'An error occurred while resetting your password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="identity-page">
        <Container maxWidth="sm">
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
              <Typography
                component="h1"
                variant="h4"
                align="center"
                sx={{
                  fontFamily: 'Bitter, serif',
                  color: '#8B3040',
                  marginBottom: 3,
                }}
              >
                RentBeets
              </Typography>

              <Alert severity="error" sx={{ marginBottom: 3 }}>
                Invalid or missing reset token
              </Alert>

              <Typography variant="body1" align="center" sx={{ marginBottom: 3 }}>
                The password reset link is invalid or has expired. Please request a new one.
              </Typography>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  href="/identity/forgot-password"
                  variant="body2"
                  sx={{ color: '#8B3040', fontWeight: 'bold' }}
                >
                  Request New Reset Link
                </Link>
              </Box>
            </Paper>
          </Box>
        </Container>
      </div>
    );
  }

  if (success) {
    return (
      <div className="identity-page">
        <Container maxWidth="sm">
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
              <Typography
                component="h1"
                variant="h4"
                align="center"
                sx={{
                  fontFamily: 'Bitter, serif',
                  color: '#8B3040',
                  marginBottom: 3,
                }}
              >
                RentBeets
              </Typography>

              <Alert severity="success" sx={{ marginBottom: 3 }}>
                Password reset successful!
              </Alert>

              <Typography variant="body1" align="center" sx={{ marginBottom: 3 }}>
                Your password has been reset successfully. You will be redirected to the sign in page shortly.
              </Typography>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  href={`${identityUrl}/identity/login?passwordReset=true`}
                  variant="body2"
                  sx={{ color: '#8B3040', fontWeight: 'bold' }}
                >
                  Sign In Now
                </Link>
              </Box>
            </Paper>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="identity-page">
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography
              component="h1"
              variant="h4"
              align="center"
              sx={{
                fontFamily: 'Bitter, serif',
                color: '#8B3040',
                marginBottom: 3,
              }}
            >
              RentBeets
            </Typography>

            <Typography
              component="h2"
              variant="h5"
              align="center"
              sx={{ marginBottom: 3 }}
            >
              Reset Password
            </Typography>

            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                autoFocus
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                error={!!validationErrors.password}
                helperText={validationErrors.password || 'Minimum 8 characters'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
                error={!!validationErrors.confirmPassword}
                helperText={validationErrors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  backgroundColor: '#8B3040',
                  '&:hover': {
                    backgroundColor: '#6d2533',
                  },
                }}
                disabled={loading}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  href={`${identityUrl}/identity/login`}
                  variant="body2"
                  sx={{ color: '#8B3040' }}
                >
                  Back to Sign In
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </div>
  );
}

export default IdentityResetPassword;
